import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DatePicker } from "@/components/ui/date-picker";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Transaction, Category } from "@/types/database";
import { X, ChevronDown, ChevronUp, User, Calendar, Clock, MessageSquare, Loader2 } from "lucide-react";
import { getCategoryIcon } from "@/constants/categoryIcons";

interface TransactionWithCategory extends Transaction {
  categoria?: Category;
}

interface EditTransactionModalProps {
  transaction: TransactionWithCategory;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Account {
  id: string;
  nome: string;
  tipo: string;
  cor: string;
}

export function AdvancedEditTransactionModal({ transaction, isOpen, onClose, onSuccess }: EditTransactionModalProps) {
  // Basic form state
  const [valor, setValor] = useState<number | string>("");
  const [descricao, setDescricao] = useState("");
  const [categoriaId, setCategoriaId] = useState("none");
  const [contaId, setContaId] = useState("none");
  const [data, setData] = useState("");
  const [dataPagamento, setDataPagamento] = useState("");
  const [observacao, setObservacao] = useState("");
  const [foiPaga, setFoiPaga] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [transactionFromDb, setTransactionFromDb] = useState<TransactionWithCategory | null>(null);

  // Recurring options state
  const [repetir, setRepetir] = useState(false);
  const [frequenciaRepeticao, setFrequenciaRepeticao] = useState("mensal");
  const [intervaloRepeticao, setIntervaloRepeticao] = useState(1);
  const [numeroTotalRepeticoes, setNumeroTotalRepeticoes] = useState(1);

  // Data and file state
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const populateFormData = useCallback((t: TransactionWithCategory) => {
    // Populate form with transaction data
    setValor(t.valor.toString());
    setDescricao(t.descricao);
    setCategoriaId(t.categoria_id || "none");
    setContaId(t.conta_id || "none");
    setObservacao(t.observacao || "");
    setFoiPaga(t.foi_paga);
    setRepetir(t.repetir);
    setFrequenciaRepeticao(t.frequencia_repeticao || "mensal");
    setIntervaloRepeticao(t.intervalo_repeticao || 1);
    setNumeroTotalRepeticoes(t.numero_total_repeticoes || 1);
    
    // Format date for input (YYYY-MM-DD) - tratar como UTC
    const dateString = t.data;
    const onlyDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    let transactionDate: Date;
    
    if (onlyDateMatch) {
      // Se for apenas data (YYYY-MM-DD), tratar como UTC
      const [y, m, d] = dateString.split('-').map(Number);
      transactionDate = new Date(Date.UTC(y, m - 1, d));
    } else {
      transactionDate = new Date(dateString);
    }
    
    const formattedDate = transactionDate.toISOString().split('T')[0];
    setData(formattedDate);
    
    // Set payment date if available
    if (t.data_pagamento) {
      const paymentDateString = t.data_pagamento;
      const paymentOnlyDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(paymentDateString);
      let paymentDate: Date;
      
      if (paymentOnlyDateMatch) {
        const [y, m, d] = paymentDateString.split('-').map(Number);
        paymentDate = new Date(Date.UTC(y, m - 1, d));
      } else {
        paymentDate = new Date(paymentDateString);
      }
      
      const formattedPaymentDate = paymentDate.toISOString().split('T')[0];
      setDataPagamento(formattedPaymentDate);
    } else {
      setDataPagamento("");
    }
  }, []);

  // Código removido - função fetchData movida para dentro do useEffect

  // Sincronizar data de pagamento com a data da transação
  useEffect(() => {
    if (data) {
      setDataPagamento(data);
    }
  }, [data]);

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setErrors({});
      setCategories([]);
      setAccounts([]);
      setTransactionFromDb(null);
      setIsInitializing(false);
      return;
    }

    let cancelled = false;
    setIsInitializing(true);

    const fetchData = async () => {
      try {
        const { data: dbTransaction, error: transactionError } = await supabase
          .from("transacoes")
          .select(`
            *,
            categoria:categorias(*)
          `)
          .eq("id", transaction.id)
          .single();

        if (transactionError) throw transactionError;
        if (!dbTransaction) throw new Error("Transação não encontrada");

        const currentTransaction = dbTransaction as unknown as TransactionWithCategory;
        const transactionCategoriaId = currentTransaction.categoria_id ?? null;
        const transactionContaId = currentTransaction.conta_id ?? null;

        const [{ data: categoriesData, error: categoriesError }, { data: accountsData, error: accountsError }] =
          await Promise.all([
            supabase
              .from("categorias")
              .select("*")
              .in("tipo", [currentTransaction.tipo, "ambos"])
              .order("nome"),
            supabase
              .from("contas")
              .select("*")
              .eq("ativo", true)
              .order("nome"),
          ]);

        if (categoriesError) throw categoriesError;
        if (accountsError) throw accountsError;

        let finalCategories = (categoriesData || []) as Category[];
        if (transactionCategoriaId && !finalCategories.some((c) => c.id === transactionCategoriaId)) {
          const { data: extraCategory, error: extraCategoryError } = await supabase
            .from("categorias")
            .select("*")
            .eq("id", transactionCategoriaId)
            .maybeSingle();
          if (extraCategoryError) throw extraCategoryError;
          if (extraCategory) {
            finalCategories = [...finalCategories, extraCategory as unknown as Category].sort((a, b) => a.nome.localeCompare(b.nome));
          }
        }

        let finalAccounts = (accountsData || []) as Account[];
        if (transactionContaId && !finalAccounts.some((a) => a.id === transactionContaId)) {
          const { data: extraAccount, error: extraAccountError } = await supabase
            .from("contas")
            .select("*")
            .eq("id", transactionContaId)
            .maybeSingle();
          if (extraAccountError) throw extraAccountError;
          if (extraAccount) {
            finalAccounts = [...finalAccounts, extraAccount as unknown as Account].sort((a, b) => a.nome.localeCompare(b.nome));
          }
        }

        if (cancelled) return;

        setTransactionFromDb(currentTransaction);
        setCategories(finalCategories);
        setAccounts(finalAccounts);
        populateFormData(currentTransaction);
        setIsExpanded(false);
        setErrors({});
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching data:", error);
        toast({
          title: "Erro ao carregar dados",
          description: "Não foi possível carregar os dados necessários.",
          variant: "destructive",
        });
        onClose();
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    }
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [isOpen, transaction.id, populateFormData, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!valor || Number(valor) <= 0) {
      newErrors.valor = "Valor deve ser maior que zero";
    }
    if (!descricao.trim()) {
      newErrors.descricao = "Descrição é obrigatória";
    }
    if (!data) {
      newErrors.data = "Data é obrigatória";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isCreditCardAccount = accounts.find(a => a.id === contaId)?.tipo === "cartao_credito";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const finalFoiPaga = isCreditCardAccount ? false : foiPaga;
      const finalDataPagamento = finalFoiPaga && dataPagamento ? new Date(dataPagamento).toISOString() : null;

      const updateData = {
        valor: Number(valor),
        descricao,
        data: new Date(data).toISOString(),
        data_pagamento: finalDataPagamento,
        categoria_id: categoriaId && categoriaId !== "none" ? categoriaId : null,
        conta_id: contaId && contaId !== "none" ? contaId : null,
        observacao: observacao || null,
        foi_paga: finalFoiPaga,
        repetir,
        frequencia_repeticao: repetir ? frequenciaRepeticao : null,
        intervalo_repeticao: repetir ? intervaloRepeticao : null,
        numero_total_repeticoes: repetir ? numeroTotalRepeticoes : null,
      };

      const { error } = await supabase
        .from("transacoes")
        .update(updateData)
        .eq("id", transaction.id);

      if (error) throw error;

      toast.success("Transação atualizada com sucesso!");

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Erro ao atualizar transação:", error);
      toast.error("Erro ao atualizar transação");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-h3">
            <User className="h-5 w-5" />
            Editar {(transactionFromDb?.tipo ?? transaction.tipo) === "entrada" ? "Receita" : "Despesa"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {isInitializing ? (
            <div className="py-12 flex items-center justify-center gap-2 text-body-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando dados...
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-body-sm font-medium text-muted-foreground">Informações Básicas</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valor" className="form-label">Valor (R$) *</Label>
                    <CurrencyInput
                      id="valor"
                      value={valor}
                      onChange={setValor}
                      placeholder="0,00"
                      className={`input-text font-medium ${errors.valor ? "border-destructive" : ""}`}
                    />
                    {errors.valor && <span className="text-caption text-destructive">{errors.valor}</span>}
                  </div>

                  <div>
                    <Label htmlFor="data" className="form-label">Data *</Label>
                    <DatePicker
                      value={data}
                      onChange={setData}
                      placeholder="Selecione a data"
                      className={`input-text ${errors.data ? "border-destructive" : ""}`}
                    />
                    {errors.data && <span className="text-caption text-destructive">{errors.data}</span>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="descricao" className="form-label">Descrição *</Label>
                  <Input
                    id="descricao"
                    placeholder="Digite a descrição"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    className={`input-text ${errors.descricao ? "border-destructive" : ""}`}
                    disabled={loading}
                  />
                  {errors.descricao && <span className="text-caption text-destructive">{errors.descricao}</span>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="categoria" className="form-label">Categoria</Label>
                    <Select value={categoriaId} onValueChange={setCategoriaId} disabled={loading}>
                      <SelectTrigger aria-label="Selecionar categoria" className="text-body">
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {categories.map((category) => {
                          const Icon = getCategoryIcon(category.icone);
                          return (
                            <SelectItem key={category.id} value={category.id}>
                              <div className="flex items-center gap-2">
                                <Icon
                                  className="w-4 h-4"
                                  style={{ color: category.cor }}
                                />
                                {category.nome}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="conta" className="form-label">Conta</Label>
                    <Select value={contaId} onValueChange={setContaId} disabled={loading}>
                      <SelectTrigger aria-label="Selecionar conta" className="text-body">
                        <SelectValue placeholder="Selecione uma conta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem conta</SelectItem>
                        {accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full border border-border"
                                style={{ backgroundColor: account.cor }}
                              />
                              {account.nome}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="foi_paga"
                      checked={isCreditCardAccount ? false : foiPaga}
                      onCheckedChange={(checked) => setFoiPaga(checked as boolean)}
                      disabled={loading || isCreditCardAccount}
                    />
                    <Label htmlFor="foi_paga" className="form-label mb-0 cursor-pointer">
                      {(transactionFromDb?.tipo ?? transaction.tipo) === "entrada" ? "Receita recebida" : "Despesa paga"}
                    </Label>
                  </div>
                  {isCreditCardAccount && (
                    <p className="text-xs text-muted-foreground">
                      Despesas no crédito são pagas através do fechamento da fatura
                    </p>
                  )}
                </div>

                {foiPaga && !isCreditCardAccount && (
                  <div>
                    <Label htmlFor="dataPagamento" className="form-label">Data do Pagamento</Label>
                    <DatePicker
                      value={dataPagamento}
                      onChange={setDataPagamento}
                      placeholder="Selecione a data do pagamento"
                      className="mt-1 input-text"
                    />
                  </div>
                )}
              </div>

              <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="ghost" className="w-full justify-between p-0 h-auto" disabled={loading}>
                    <span className="text-body-sm font-medium text-muted-foreground">Detalhes Adicionais</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>

                <CollapsibleContent className="space-y-4 mt-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="observacao" className="form-label mb-0">Observação</Label>
                    </div>
                    <Textarea
                      id="observacao"
                      placeholder="Adicione uma observação (opcional)"
                      value={observacao}
                      onChange={(e) => setObservacao(e.target.value)}
                      rows={3}
                      readOnly
                      disabled={loading}
                      className="text-body"
                      onClick={(e) => {
                        e.currentTarget.readOnly = false;
                        e.currentTarget.focus();
                      }}
                      onBlur={(e) => {
                        e.currentTarget.readOnly = true;
                      }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Label className="form-label mb-0">Opções de Recorrência</Label>
                    </div>

                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox
                        id="repetir"
                        checked={repetir}
                        onCheckedChange={(checked) => setRepetir(checked as boolean)}
                        disabled={loading}
                      />
                      <Label htmlFor="repetir" className="form-label mb-0 cursor-pointer">
                        {(transactionFromDb?.tipo ?? transaction.tipo) === "entrada" ? "Receita recorrente" : "Despesa recorrente"}
                      </Label>
                    </div>

                    {repetir && (
                      <div className="space-y-4 pl-6 border-l-2 border-muted">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="frequencia" className="form-label">Frequência</Label>
                            <Select value={frequenciaRepeticao} onValueChange={setFrequenciaRepeticao} disabled={loading}>
                              <SelectTrigger className="text-body">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="diario">Diário</SelectItem>
                                <SelectItem value="semanal">Semanal</SelectItem>
                                <SelectItem value="mensal">Mensal</SelectItem>
                                <SelectItem value="anual">Anual</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="intervalo" className="form-label">Intervalo</Label>
                            <Input
                              id="intervalo"
                              type="number"
                              min="1"
                              value={intervaloRepeticao}
                              onChange={(e) => setIntervaloRepeticao(parseInt(e.target.value))}
                              placeholder="1"
                              disabled={loading}
                              className="input-text"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="total-repeticoes" className="form-label">Número de repetições</Label>
                          <Input
                            id="total-repeticoes"
                            type="text"
                            value={numeroTotalRepeticoes === 12 ? "" : numeroTotalRepeticoes.toString()}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, "");
                              setNumeroTotalRepeticoes(value ? parseInt(value) : 12);
                            }}
                            placeholder="12"
                            inputMode="numeric"
                            disabled={loading}
                            className="input-text"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 btn-label">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading || isInitializing} 
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 btn-label"
            >
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
